import type { Addon, AddonCategory, SelectedAddon } from '@/modules/stores/types/addons';

/**
 * Encontra um addon específico em todas as categorias
 */
export function findAddonById(addonId: string, addonCategories: AddonCategory[]): Addon | null {
  for (const category of addonCategories) {
    const addon = category.addons.find(a => a.id === addonId);
    if (addon) return addon;
  }
  return null;
}

/**
 * Encontra uma categoria de addon pelo ID
 */
export function findAddonCategoryById(
  categoryId: string,
  addonCategories: AddonCategory[]
): AddonCategory | null {
  return addonCategories.find(cat => cat.id === categoryId) || null;
}

/**
 * Resolve a categoria à qual um adicional selecionado pertence DENTRO deste
 * produto — a base para consumir o preço/regra efetivos por produto no cálculo
 * e na validação do carrinho.
 *
 * Prioriza o vínculo capturado no momento da seleção
 * (`selectedAddon.addon_category_id`), ou seja, a categoria em que o adicional
 * foi de fato escolhido. Só recorre à busca global por id (`findAddonById`)
 * como fallback para seleções antigas sem esse vínculo. Isso corrige o "vínculo
 * addon-produto incorreto": quando o mesmo adicional aparece em mais de uma
 * categoria do produto, a busca global casaria com a primeira ocorrência e
 * aplicaria a regra/preço da categoria errada.
 *
 * Nunca confia cegamente no id recebido: valida que a categoria realmente
 * pertence ao produto antes de usá-la (default seguro — dado inválido/defasado
 * cai no fallback em vez de contaminar o cálculo).
 */
export function getSelectedAddonCategoryId(
  selectedAddon: SelectedAddon,
  addonCategories: AddonCategory[]
): string | null {
  const linkedCategoryId = selectedAddon.addon_category_id;
  if (linkedCategoryId && findAddonCategoryById(linkedCategoryId, addonCategories)) {
    return linkedCategoryId;
  }

  return findAddonById(selectedAddon.id, addonCategories)?.addon_category_id ?? null;
}

/**
 * Obtém a quantidade de um addon específico
 */
export function getAddonQuantity(addonId: string, selectedAddons: SelectedAddon[]): number {
  const addon = selectedAddons.find(addon => addon.id === addonId);
  return addon?.quantity || 0;
}

/**
 * Verifica se um addon está selecionado
 */
export function isAddonSelected(addonId: string, selectedAddons: SelectedAddon[]): boolean {
  return selectedAddons.some(addon => addon.id === addonId);
}

/**
 * Calcula o total de addons selecionados em uma categoria
 */
export function getTotalSelectedInCategory(
  categoryId: string,
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[]
): number {
  return selectedAddons
    .filter(addon => getSelectedAddonCategoryId(addon, addonCategories) === categoryId)
    .reduce((total, addon) => total + addon.quantity, 0);
}

/**
 * Valida se a seleção de addons está dentro dos limites da categoria
 */
export function validateAddonSelection(
  categoryId: string,
  newQuantity: number,
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[]
): { isValid: boolean; message: string } {
  const category = findAddonCategoryById(categoryId, addonCategories);
  if (!category || category.select_max === 0) {
    return { isValid: true, message: '' };
  }

  const currentTotal = getTotalSelectedInCategory(categoryId, selectedAddons, addonCategories);

  if (currentTotal + newQuantity > category.select_max) {
    return {
      isValid: false,
      message: `Máximo de ${category.select_max} ${
        category.select_max === 1 ? 'item' : 'itens'
      } permitido nesta categoria`,
    };
  }

  return { isValid: true, message: '' };
}

/**
 * Verifica se os requisitos mínimos das categorias foram atendidos
 * Considera tanto o campo mandatory quanto select_min
 * 
 * Regras:
 * 1. Se mandatory === 1: precisa ter pelo menos select_min itens (ou 1 se select_min === 0)
 * 2. Se mandatory === 0 e select_min > 0: só valida se o usuário começou a selecionar itens
 */
export function checkMinimumRequirements(
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[]
): { isValid: boolean; message: string; categoryId?: string } {
  for (const category of addonCategories) {
    const totalSelected = getTotalSelectedInCategory(
      category.id,
      selectedAddons,
      addonCategories
    );

    // Calcula o mínimo necessário
    const minRequired = category.select_min > 0 ? category.select_min : (category.mandatory === 1 ? 1 : 0);

    // Se a categoria é obrigatória, precisa ter pelo menos o mínimo
    if (category.mandatory === 1 && totalSelected < minRequired) {
      return {
        isValid: false,
        message: minRequired === 1 
          ? `${category.name}: Esta categoria é obrigatória. Selecione pelo menos 1 item`
          : `${category.name}: Selecione pelo menos ${minRequired} ${minRequired === 1 ? 'item' : 'itens'}`,
        categoryId: category.id,
      };
    }

    // Se a categoria não é obrigatória mas tem select_min,
    // só valida se o usuário começou a selecionar itens (totalSelected > 0)
    if (category.mandatory !== 1 && category.select_min > 0 && totalSelected > 0 && totalSelected < category.select_min) {
      return {
        isValid: false,
        message: `${category.name}: Selecione pelo menos ${category.select_min} ${category.select_min === 1 ? 'item' : 'itens'}`,
        categoryId: category.id,
      };
    }
  }

  return { isValid: true, message: '' };
}

/**
 * Verifica se alguma categoria excedeu o limite máximo (select_max),
 * considerando a SOMA TOTAL das quantidades selecionadas na categoria
 * (e não apenas a quantidade de adicionais distintos).
 *
 * Regras:
 * 1. select_max === 0 significa categoria sem limite (ignorada).
 * 2. A soma das quantidades de todos os adicionais da categoria não pode
 *    ultrapassar select_max.
 */
export function checkMaximumRequirements(
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[]
): { isValid: boolean; message: string; categoryId?: string } {
  for (const category of addonCategories) {
    // select_max === 0 (ou ausente) representa categoria sem teto
    if (category.select_max <= 0) continue;

    const totalSelected = getTotalSelectedInCategory(
      category.id,
      selectedAddons,
      addonCategories
    );

    if (totalSelected > category.select_max) {
      return {
        isValid: false,
        message: `${category.name}: Selecione no máximo ${category.select_max} ${
          category.select_max === 1 ? 'item' : 'itens'
        }`,
        categoryId: category.id,
      };
    }
  }

  return { isValid: true, message: '' };
}

/**
 * Calcula o limite máximo para um addon específico, considerando DOIS tetos
 * independentes (issue 311):
 *
 * 1. Teto do grupo (`selectMax` da categoria): a soma das quantidades da
 *    categoria não pode ultrapassar esse valor, então este addon pode crescer no
 *    máximo `selectMax - (outros addons da categoria)`. `selectMax === 0` indica
 *    grupo sem teto.
 * 2. Teto individual do complemento (`addon.select_max`): quantidade máxima que
 *    ESTE complemento pode ser escolhido, independente do teto do grupo.
 *    `0`/ausente indica complemento sem teto individual.
 *
 * O máximo efetivo é o MENOR entre os tetos aplicáveis. Assim o "+" é bloqueado
 * ao atingir o teto individual mesmo quando o grupo ainda teria espaço (ou não
 * tem teto). Sem nenhum teto aplicável, mantém o comportamento anterior
 * (praticamente ilimitado).
 */
export function getMaxForAddon(
  addonId: string,
  categoryId: string,
  selectMax: number,
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[]
): number {
  const category = findAddonCategoryById(categoryId, addonCategories);
  if (!category) return 0;

  // Teto do grupo: espaço restante na categoria (excluindo o addon atual).
  // selectMax <= 0 => grupo sem teto.
  const groupRemaining =
    selectMax > 0
      ? Math.max(
          0,
          selectMax -
            category.addons.reduce((total, catAddon) => {
              if (catAddon.id === addonId) return total;
              return total + getAddonQuantity(catAddon.id, selectedAddons);
            }, 0)
        )
      : Infinity;

  // Teto individual do complemento: limite próprio, independente do grupo.
  // select_max <= 0 (ou ausente) => complemento sem teto individual.
  const addonMax = category.addons.find(catAddon => catAddon.id === addonId)?.select_max ?? 0;
  const individualMax = addonMax > 0 ? addonMax : Infinity;

  const effectiveMax = Math.min(groupRemaining, individualMax);

  // Nenhum teto aplicável: preserva o "praticamente ilimitado" anterior.
  return Number.isFinite(effectiveMax) ? effectiveMax : 999;
}

/**
 * Mensagem padrão exibida ao cliente quando ele tenta ultrapassar o limite
 * máximo de complementos de uma categoria (o "máximo por complemento escolhido",
 * issue 311) durante o fluxo de pedido.
 *
 * Complementa o comportamento de "bloquear" (o controle de quantidade já impede
 * a seleção acima do teto) com o de "avisar": explica o motivo quando o cliente
 * toca um complemento já no limite da categoria.
 */
export function getCategoryLimitMessage(selectMax: number): string {
  return `Máximo de ${selectMax} ${selectMax === 1 ? 'item' : 'itens'} nesta categoria`;
}

/**
 * Legenda do teto individual do complemento (o "máximo por complemento
 * escolhido", issue 311): quantidade máxima que ESTE complemento pode ser
 * escolhido, independente do teto do grupo (`AddonCategory.select_max`).
 *
 * Usada tanto como legenda sob o nome do complemento quanto como descrição do
 * aviso exibido ao tentar ultrapassar o limite no click. `select_max <= 0`
 * (sem teto individual) retorna string vazia para não poluir o card.
 */
export function getAddonLimitLabel(selectMax: number | undefined): string {
  if (!selectMax || selectMax <= 0) return '';
  return `Máximo ${selectMax} ${selectMax === 1 ? 'unidade' : 'unidades'}`;
}

/**
 * Normaliza o teto individual do complemento (`select_max` por complemento, o
 * "máximo por complemento escolhido" da issue 311) vindo do endpoint de itens do
 * App (`merchants/{slug}/items`).
 *
 * A API pode enviar o valor como número, string numérica (ex.: `"2"`) ou
 * ausente/nulo. Sem essa normalização, um `"0"` (string) chegaria ao seletor
 * como valor "truthy" e bloquearia o complemento indevidamente. Aqui a entrada
 * não confiável é coagida a um inteiro `>= 0`; qualquer valor inválido
 * (não numérico, negativo ou não finito) vira `0` (sem teto individual) —
 * default seguro que preserva a compatibilidade com respostas antigas.
 */
export function normalizeAddonSelectMax(value: unknown): number {
  const parsed = typeof value === 'string' ? Number(value.trim()) : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.floor(parsed);
}

/**
 * Obtém o tipo de cálculo de preço da categoria com fallback para compatibilidade
 * 
 * @param category - Categoria de addon
 * @returns 0 = soma normal, 1 = preço maior, 2 = preço médio
 */
export function getPriceCalculationType(category: AddonCategory): number {
  return category.price_calculation_type ?? 
    (category.consider_most_expensive === 1 ? 1 : 0);
}

/**
 * Retorna o label descritivo do tipo de cálculo de preço
 * 
 * @param type - Tipo de cálculo (0, 1 ou 2)
 * @returns Label para exibição na UI ou string vazia para soma normal
 */
export function getCalculationTypeLabel(type: number): string {
  switch (type) {
    case 1:
      return 'Cobra apenas o mais caro';
    case 2:
      return 'Preço médio dos selecionados';
    default:
      return '';
  }
}

/**
 * Calcula o preço de uma categoria específica baseado no tipo de cálculo
 * 
 * @param category - Categoria de addon
 * @param selectedAddons - Addons selecionados da categoria
 * @returns Preço calculado da categoria
 */
export function calculateCategoryPrice(
  category: AddonCategory,
  selectedAddons: SelectedAddon[]
): number {
  if (selectedAddons.length === 0) {
    return 0;
  }

  const calcType = getPriceCalculationType(category);
  const prices = selectedAddons.map(addon => addon.price * addon.quantity);

  switch (calcType) {
    case 1: {
      // Preço maior: cobra apenas o adicional de maior valor UNITÁRIO,
      // independente da quantidade selecionada.
      // Ex.: pizza meio a meio com 2x Abacaxi (R$87,90) + 1x Bacon (R$134,53)
      // cobra o sabor mais caro (R$134,53), e não 87,90×2 = R$175,80.
      const unitPrices = selectedAddons.map(addon => addon.price);
      return Math.max(...unitPrices);
    }

    case 2: {
      // Média PONDERADA: soma ponderada ÷ total de PARTES
      // Exemplo pizza 4 sabores: 2x Abacaxi (R$87,90) + 1x Bacon (R$134,53) + 1x Atum (R$31,97)
      // Total partes = 2 + 1 + 1 = 4
      // Soma ponderada = (87,90×2) + (134,53×1) + (31,97×1) = 342,30
      // Média = 342,30 / 4 = R$ 85,57
      const totalParts = selectedAddons.reduce((sum, addon) => sum + addon.quantity, 0);
      const weightedSum = prices.reduce((a, b) => a + b, 0);
      return weightedSum / totalParts;
    }
    
    default:
      // Soma normal (calcType === 0)
      return prices.reduce((a, b) => a + b, 0);
  }
}

/**
 * Calcula o preço total dos addons considerando o campo price_calculation_type
 * 
 * Tipos de cálculo:
 * - 0: Soma normal (soma todos os valores)
 * - 1: Preço maior (considera apenas o mais caro)
 * - 2: Preço médio (média dos valores selecionados)
 * 
 * Fallback para consider_most_expensive se price_calculation_type não existir.
 */
export function calculateAddonsPrice(
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[]
): number {
  // Agrupa os addons selecionados por categoria
  const addonsByCategory = new Map<string, SelectedAddon[]>();

  selectedAddons.forEach(selectedAddon => {
    // Usa a categoria vinculada à seleção (com fallback por id) para não aplicar
    // a regra de preço de uma categoria errada quando o adicional é ambíguo.
    const categoryId = getSelectedAddonCategoryId(selectedAddon, addonCategories);
    if (!categoryId) return;

    if (!addonsByCategory.has(categoryId)) {
      addonsByCategory.set(categoryId, []);
    }
    addonsByCategory.get(categoryId)!.push(selectedAddon);
  });

  // Calcula o preço total considerando price_calculation_type
  let totalPrice = 0;

  addonsByCategory.forEach((addons, categoryId) => {
    const category = findAddonCategoryById(categoryId, addonCategories);
    if (!category) return;

    totalPrice += calculateCategoryPrice(category, addons);
  });

  return totalPrice;
}

/**
 * Aplica a mudança de quantidade de um complemento sobre a seleção atual e
 * devolve a nova seleção (sempre imutável). É a regra de `useAddonManagement`
 * em forma pura, reaproveitada pelos complementos das escolhas de combo
 * (PRAT-20), que têm uma seleção por produto escolhido.
 *
 * - Rádio (`isRadio` + `categoryId`): remove os da categoria e marca o novo.
 * - Quantidade: atualiza/remove o complemento ou o inclui na seleção.
 */
export function applyAddonChange(
  selectedAddons: SelectedAddon[],
  addonCategories: AddonCategory[],
  addonId: string,
  quantity: number,
  isRadio = false,
  categoryId?: string
): SelectedAddon[] {
  const addonData = findAddonById(addonId, addonCategories);
  if (!addonData) return selectedAddons;

  if (isRadio && categoryId) {
    // Usa o vínculo registrado na seleção (com fallback por id) para não remover
    // itens de outra categoria quando o mesmo adicional é compartilhado.
    const filteredAddons = selectedAddons.filter(
      addon => getSelectedAddonCategoryId(addon, addonCategories) !== categoryId
    );

    return quantity > 0
      ? [
          ...filteredAddons,
          {
            id: addonId,
            name: addonData.name,
            price: Number(addonData.price || 0),
            quantity: 1,
            addon_category_id: categoryId,
          },
        ]
      : filteredAddons;
  }

  const existingIndex = selectedAddons.findIndex(addon => addon.id === addonId);

  if (existingIndex >= 0) {
    return quantity > 0
      ? selectedAddons.map((addon, index) =>
          index === existingIndex ? { ...addon, quantity } : addon
        )
      : selectedAddons.filter((_, index) => index !== existingIndex);
  }

  if (quantity <= 0) return selectedAddons;

  return [
    ...selectedAddons,
    {
      id: addonId,
      name: addonData.name,
      price: Number(addonData.price || 0),
      quantity,
      addon_category_id: categoryId ?? addonData.addon_category_id,
    },
  ];
}
